/**
 * Audio plan — the platform-independent format for "play this audio."
 *
 * Generators produce these. Audio engines (web, mobile) consume them.
 * No musical knowledge lives here — just timing, pitch (as MIDI), and gain.
 *
 * The plan is a flat list of NoteEvents with absolute start times in seconds.
 * Polyphonic events (chords) are simply multiple events with the same startTime.
 * Sequential events (melodies) have monotonically increasing startTimes.
 */

export interface NoteEvent {
  /** MIDI note number. 60 = middle C, 69 = A4 = 440 Hz. */
  midi: number;
  /** When to start, in seconds from t=0. */
  startTime: number;
  /** How long the note sounds, in seconds. */
  duration: number;
  /** Linear gain 0..1. Multi-note chords typically use lower per-voice gain. */
  gain: number;
  /**
   * Optional voice tag — useful for the audio engine to route different events
   * to different timbres (e.g. melody = bright, accompaniment = soft).
   */
  voice?: "melody" | "harmony" | "bass" | "target";
}

export interface AudioPlan {
  events: NoteEvent[];
  /** Total length in seconds — when does the audio finish. */
  totalDuration: number;
  /** A4 reference frequency in Hz. Default 440. */
  a4?: number;
}

/** Helper: build an empty plan and append events to it. */
export class AudioPlanBuilder {
  private events: NoteEvent[] = [];
  private cursor = 0;

  /** Add a single note at the current cursor; cursor doesn't advance. */
  addNote(midi: number, duration: number, gain = 0.18, voice?: NoteEvent["voice"]): this {
    this.events.push({ midi, startTime: this.cursor, duration, gain, voice });
    return this;
  }

  /** Add a chord (multiple simultaneous notes); cursor doesn't advance. */
  addChord(midis: number[], duration: number, gainPerNote = 0.08, voice?: NoteEvent["voice"]): this {
    midis.forEach((m) => this.addNote(m, duration, gainPerNote, voice));
    return this;
  }

  /** Advance the cursor by `seconds`. */
  advance(seconds: number): this {
    this.cursor += seconds;
    return this;
  }

  /** Get the current cursor position. */
  now(): number {
    return this.cursor;
  }

  /** Build the final plan. */
  build(): AudioPlan {
    const totalDuration = this.events.length === 0
      ? 0
      : Math.max(...this.events.map((e) => e.startTime + e.duration));
    return { events: [...this.events].sort((a, b) => a.startTime - b.startTime), totalDuration };
  }
}
